import { useMemo, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Select,
  Tag,
  Button,
  Drawer,
  Modal,
  message,
  Alert,
  Checkbox,
  Spin,
  Tooltip,
  Space,
  Typography,
} from 'antd';
import {
  DeleteOutlined,
  CopyOutlined,
  EditOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useOperatorStore } from '../../store/useOperatorStore';
import WarningMessage from '../../components/WarningMessage';
import { useIsEditAllowedWithOperator } from '../../utils/utils';
import {
  useFetchTouPricingList,
  useCopyTouPricing,
  useDeleteTouPricing,
} from './Hooks';
import TouInlineEditor from './TouInlineEditor';

const { Title } = Typography;

const getYearOptions = () => {
  const current = dayjs().year();
  const options = [];
  for (let y = current - 2; y <= current + 5; y++) {
    options.push({ label: String(y), value: y });
  }
  return options;
};

const renderStatusTag = (m, t) => {
  if (m.status === 'COMPLETE') return <Tag color="green">{t('complete')}</Tag>;
  if (m.status === 'HAS_GAPS') return <Tag color="orange">{t('hasGaps')}</Tag>;
  return <Tag>{t('notSet')}</Tag>;
};

const TouPricingPage = () => {
  const { t } = useTranslation('touPricing');
  const { operatorId } = useOperatorStore();
  const isEditAllowed = useIsEditAllowedWithOperator();
  const [year, setYear] = useState(dayjs().year());
  const [editMonth, setEditMonth] = useState(null);
  const [editToolbar, setEditToolbar] = useState(null);
  const [copyMonth, setCopyMonth] = useState(null);
  const [copyTargets, setCopyTargets] = useState([]);

  const { data, isLoading, isFetching, refetch, isError, error } = useFetchTouPricingList(
    operatorId,
    year,
  );
  const copyMutation = useCopyTouPricing();
  const deleteMutation = useDeleteTouPricing();

  const months = data?.months || [];
  const yearOptions = useMemo(() => getYearOptions(), []);

  const closeEditDrawer = () => {
    setEditMonth(null);
    setEditToolbar(null);
  };

  const handleCopy = async () => {
    try {
      if (!copyTargets.length) {
        message.warning(t('validation.copyTargets'));
        return;
      }
      const targets = [];
      for (const m of copyTargets) {
        targets.push({ year, month: m });
      }
      await copyMutation.mutateAsync({
        operatorId,
        sourceYear: year,
        sourceMonth: copyMonth,
        targets,
      });
      message.success(t('copySuccess'));
      setCopyMonth(null);
      setCopyTargets([]);
      refetch();
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || 'Error');
    }
  };

  const confirmDelete = async (tariffId) => {
    await deleteMutation.mutateAsync(tariffId);
    message.success(t('deleteSuccess'));
    refetch();
  };

  const handleDelete = (monthItem) => {
    if (!monthItem.tariffId) return;
    Modal.confirm({
      title: t('deleteConfirm'),
      onOk: () => confirmDelete(monthItem.tariffId),
    });
  };

  if (!operatorId) {
    return <WarningMessage />;
  }

  return (
    <Space orientation="vertical" size="middle" style={{ display: 'flex', width: '100%' }}>
      <Space>
        <Select
          value={year}
          onChange={setYear}
          options={yearOptions}
          style={{ width: 120 }}
          placeholder={t('year')}
          showSearch
          optionFilterProp="label"
        />
        <Tooltip title={t('refresh')}>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching} />
        </Tooltip>
      </Space>

      {isError && (
        <Alert
          type="error"
          showIcon
          message={error?.response?.data?.message || error?.message || t('loadError')}
        />
      )}

      <Spin spinning={isLoading}>
        <Row gutter={[12, 12]}>
          {months.map((m) => (
            <Col xs={24} sm={12} md={8} lg={6} key={m.month}>
              <Card
                size="small"
                title={t(`months.${m.month}`)}
                extra={renderStatusTag(m, t)}
                actions={
                  isEditAllowed
                    ? [
                        <EditOutlined key="edit" onClick={() => setEditMonth(m.month)} />,
                        <CopyOutlined
                          key="copy"
                          onClick={() => {
                            if (!m.tariffId) {
                              message.warning(t('notSet'));
                              return;
                            }
                            setCopyMonth(m.month);
                            setCopyTargets([]);
                          }}
                        />,
                        <DeleteOutlined
                          key="delete"
                          onClick={() => handleDelete(m)}
                          style={{ color: m.tariffId ? undefined : '#ccc' }}
                        />,
                      ]
                    : []
                }
              >
                <Typography.Text type="secondary">
                  {t('periods')}: {m.periodCount || 0}
                </Typography.Text>
                <br />
                <Typography.Text type="secondary">
                  {t('slots')}: {m.slotCount || 0}
                </Typography.Text>
              </Card>
            </Col>
          ))}
        </Row>

        {!isLoading && !isError && months.length === 0 && (
          <Typography.Text
            type="secondary"
            style={{ display: 'block', textAlign: 'center', padding: 48 }}
          >
            {t('empty')}
          </Typography.Text>
        )}
      </Spin>

      <Drawer
        title={
          <Title level={4} className="mb-0!">
            {`${t('edit')} — ${editMonth ? t(`months.${editMonth}`) : ''} ${year}`}
          </Title>
        }
        open={!!editMonth}
        onClose={closeEditDrawer}
        destroyOnClose
        // Keep drawer below time-picker portal (z-index 2200) so the panel is not clipped
        zIndex={1000}
        styles={{
          wrapper: {
            width: Math.min(1120, typeof window !== 'undefined' ? window.innerWidth * 0.94 : 1120),
          },
          body: { paddingTop: 12, overflow: 'auto' },
        }}
        extra={isEditAllowed ? editToolbar : null}
      >
        <Typography.Text type="secondary" className="mb-3! block text-sm">
          {t('editWarning')}
        </Typography.Text>
        {editMonth ? (
          <TouInlineEditor
            key={`${operatorId}-${year}-${editMonth}`}
            operatorId={operatorId}
            year={year}
            month={editMonth}
            onToolbarChange={setEditToolbar}
            onSaved={() => {
              closeEditDrawer();
              refetch();
            }}
          />
        ) : null}
      </Drawer>

      <Modal
        title={<Title level={4} className="mb-0!">{t('copyTitle')}</Title>}
        open={!!copyMonth}
        onCancel={() => setCopyMonth(null)}
        onOk={handleCopy}
        confirmLoading={copyMutation.isPending}
      >
        <Space orientation="vertical" size="small" style={{ display: 'flex', width: '100%' }}>
          <Typography.Text>
            {t('copySource')}: {copyMonth ? t(`months.${copyMonth}`) : ''}
          </Typography.Text>
          <Checkbox.Group
            value={copyTargets}
            onChange={setCopyTargets}
            options={Array.from({ length: 12 }, (_, i) => i + 1)
              .filter((m) => m !== copyMonth)
              .map((m) => ({ label: t(`months.${m}`), value: m }))}
            style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
          />
        </Space>
      </Modal>
    </Space>
  );
};

export default TouPricingPage;
